// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { color, ellipsis } from "@werner/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasCategoryContext;

export interface CategoryDetailLinkListStyles {
    container?: GridContainerProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // XNG-Changes
    titleText?: TypographyPresentationProps;
}

export const categoryDetailLinkListStyles: CategoryDetailLinkListStyles = {
    container: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            padding-top: 9px;

            & > .subCategoryNameLink:not(:last-child) {
                margin-bottom: 20px;
            }
        `,
    },
    titleText: {
        size: 20,
        weight: 500,
        css: css`
            display: block;
            margin-bottom: 16px;
        `,
    },
    subCategoryNameLink: {
        color: "common.ui3",
        typographyProps: {
            size: 16,
            css: css`
                ${ellipsis(2)}

                font-weight: normal;
                &&&:hover {
                    color: ${color("text.link")};
                }
            `,
        },
        css: css`
            display: block;
        `,
    },
};

const styles = categoryDetailLinkListStyles;

const CategoryDetailLinkList = ({ category }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Typography forwardAs="label" htmlFor="productsKeywordSearch" {...styles.titleText}>
                {`${translate("Shop")} ${category.shortDescription ?? category.name}`}
            </Typography>
            {category.subCategoryIds.map(subCategoryId => (
                <SubCategoryLink subCategoryId={subCategoryId} key={subCategoryId.toString()} />
            ))}
        </StyledWrapper>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { subCategoryId: string }) => {
    return {
        category: getCategoryState(state, ownProps.subCategoryId).value,
    };
};

const SubCategoryLinkView = ({ category }: ReturnType<typeof mapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link
            href={category.path}
            {...styles.subCategoryNameLink}
            className="subCategoryNameLink"
            data-test-selector={`categoryDetailsLinkListLink_${category.id}`}
            aria-label={`${category.shortDescription} Category Link`}
        >
            {category.shortDescription}
        </Link>
    );
};

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailLinkList),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
